/**
 * Hit-testing — canvas point → entity id.
 *
 * Pure. Uses the WP6 projection (`worldToScreen` with the same
 * `ProjectionCenter` the renderer uses) so the hitbox matches what the
 * player sees.
 *
 * Overlap resolution rule per wp7-client-ui-input.md § "Tests": pick
 * the topmost entity in the same depth-sort order WP6 draws with — since #730
 * that is `entity.y` ascending (screen depth is world `y` under the
 * axis-aligned basis; it was `x + y` while the basis was rotated), so the
 * highest-y (drawn last) wins. Reusing that sort ensures "click what you see
 * on top"; it must stay identical to `entityView.renderFrame`'s sort and
 * `arena.pillarDepthKey`.
 */

import type { WireSimState } from "@pit/protocol";
import {
  BODY_MAGNIFICATION,
  projectRadius,
  worldToScreen,
  type ProjectionCenter,
} from "../render/projection.js";

/** Screen point (canvas-relative). */
export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

/** Hitbox record for overlap resolution. */
interface HitBox {
  readonly entityId: string;
  readonly zSort: number;
}

/**
 * Test entities under a screen point. Returns the `entityId` of the
 * topmost hit, or `null`.
 *
 * Each entity is tested against its OWN wire-carried `radius` (#342 — the
 * live, aura-modified body radius; a `statMod` on `"radius"`, e.g. Stand
 * Behind Me's +30%, now hit-tests correctly instead of every entity sharing
 * one fixed constant). The projected screen radius matches how the renderer
 * draws bodies (see entityView.ts — `projectRadius(radius) *
 * BODY_MAGNIFICATION`, #277). We use the SAME magnification constant so the
 * clickable area always matches the drawn area.
 *
 * Dead entities are excluded — corpses shouldn't intercept target clicks.
 */
export function hitTestEntity(
  point: ScreenPoint,
  snapshot: WireSimState,
  center: ProjectionCenter,
): string | null {
  let best: HitBox | null = null;
  for (const e of snapshot.entities) {
    if (!e.alive) continue;
    // Match entityView.ts: rPx = max(3, projectRadius(r) * BODY_MAGNIFICATION),
    // using THIS entity's own live radius. We reuse the SAME projection
    // helper + magnification constant so the clickable area can never drift
    // from the drawn area (e.g. when the zoom / ISO_SCALE_X changes).
    // projectRadius pulls no Pixi/DOM in, so the input layer stays free of
    // render-runtime deps.
    //
    // The 3px floor is applied in CONTAINER space and the viewport `zoom`
    // multiplied after (#730), because that is the order Pixi produces on
    // screen: `entityView` draws `max(3, projected)` into a container the
    // camera scales by `zoom`. Flooring after the multiply would make a
    // tiny-radius entity's hitbox smaller than its drawn circle.
    const projected = projectRadius(e.radius) * BODY_MAGNIFICATION;
    const rPx = (projected < 3 ? 3 : projected) * center.zoom;
    const rSq = rPx * rPx;
    const s = worldToScreen(e.x, e.y, center);
    const dx = point.x - s.sx;
    const dy = point.y - s.sy;
    if (dx * dx + dy * dy <= rSq) {
      const zSort = e.y;
      if (best === null || zSort > best.zSort) {
        best = { entityId: e.entityId, zSort };
      }
    }
  }
  return best === null ? null : best.entityId;
}
